/*
  Dealer portal auth — talks to the Supabase-backed API on dealer.quellworkspaces.com.

  Shopify's own customer accounts are not involved. The API sets an HttpOnly
  session cookie on the shared parent domain, so every call here must send
  credentials; the storefront and the API are same-site but cross-origin, which
  is why this is a credentialed CORS request rather than a plain fetch.

  Handles four things, each only if its markup is on the page: the sign-in form,
  the "forgot password" form, the set-password page that an emailed link lands
  on, and sign-out links.
*/
(function () {
  'use strict';

  var API = window.__dealerApi;
  if (!API) return;

  var reqId = 'd' + Date.now();
  var strings = (window.theme && window.theme.strings) || {};
  var GENERIC_ERROR = strings.errorGeneric || 'Something went wrong. Please try again.';

  function post(path, body) {
    return fetch(API + path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          return { ok: res.ok, status: res.status, data: data };
        });
    });
  }

  function setMessage(form, text, isError) {
    var box = form.querySelector('[data-auth-message]');
    if (!box) return;
    box.textContent = text || '';
    box.hidden = !text;
    box.classList.toggle('ca-note--error', Boolean(isError));
    box.classList.toggle('ca-note--ok', Boolean(text) && !isError);
  }

  function setBusy(form, busy) {
    var button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = busy;
    form.classList.toggle('is-busy', busy);
  }

  /*
    A network failure and a 500 read the same to someone signing in, so both get
    the same wording. Anything the API chose to say is shown as-is: those strings
    are written for dealers.
  */
  function failureText(result) {
    return (result && result.data && result.data.error) || GENERIC_ERROR;
  }

  function handle(form, path, buildBody, onSuccess) {
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var body = buildBody(form);
      if (!body) return;

      setMessage(form, '');
      setBusy(form, true);

      post(path, body)
        .then(function (result) {
          if (!result.ok) {
            console.warn('[DealerAuth ' + reqId + '] ' + path + ' rejected', result.status);
            setMessage(form, failureText(result), true);
            setBusy(form, false);
            return;
          }
          onSuccess(form, result.data);
        })
        .catch(function (err) {
          console.error('[DealerAuth ' + reqId + '] ' + path + ' failed', err);
          setMessage(form, GENERIC_ERROR, true);
          setBusy(form, false);
        });
    });
  }

  function fallbackPortalUrl() {
    return '/pages/dealer';
  }

  /* ---- Sign in ---- */
  var loginForm = document.querySelector('[data-dealer-login]');
  if (loginForm) {
    handle(
      loginForm,
      '/api/dealer/login',
      function (form) {
        return {
          email: form.querySelector('[name="email"]').value.trim(),
          password: form.querySelector('[name="password"]').value
        };
      },
      function (form, data) {
        window.location.assign(data.redirectTo || fallbackPortalUrl());
      }
    );
  }

  /* ---- Forgot password ---- */
  var recoverForm = document.querySelector('[data-dealer-recover]');
  if (recoverForm) {
    handle(
      recoverForm,
      '/api/dealer/recover',
      function (form) {
        return { email: form.querySelector('[name="email"]').value.trim() };
      },
      function (form, data) {
        // Deliberately the same answer whether or not the address has an
        // account, matching the API — this must not reveal who is a dealer.
        setMessage(form, data.message || strings.dealerResetSent || 'If that address has a dealer account, a reset link is on its way.');
        setBusy(form, false);
      }
    );
  }

  /* ---- Set password (landing page for an emailed link) ---- */
  var setPasswordForm = document.querySelector('[data-dealer-set-password]');
  if (setPasswordForm) {
    // Supabase returns the tokens in the URL fragment, which never reaches a
    // server. Read them here, then strip them from the address bar so they do
    // not linger in history or get copied out of a shared URL.
    var params = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    var accessToken = params.get('access_token') || '';
    var refreshToken = params.get('refresh_token') || '';
    var linkError = params.get('error_description') || params.get('error') || '';

    if (accessToken && refreshToken) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    if (linkError || !accessToken || !refreshToken) {
      setMessage(
        setPasswordForm,
        linkError || strings.dealerLinkInvalid || 'This link is no longer valid. Request a new one from the sign-in page.',
        true
      );
      setBusy(setPasswordForm, true);
    } else {
      handle(
        setPasswordForm,
        '/api/dealer/set-password',
        function (form) {
          var password = form.querySelector('[name="password"]').value;
          var confirmField = form.querySelector('[name="password_confirm"]');
          if (confirmField && confirmField.value !== password) {
            setMessage(form, strings.dealerPasswordMismatch || 'Both passwords must match.', true);
            return null;
          }
          return { access_token: accessToken, refresh_token: refreshToken, password: password };
        },
        function (form, data) {
          window.location.assign(data.redirectTo || fallbackPortalUrl());
        }
      );
    }
  }

  /* ---- Sign out ---- */
  var signOutLinks = document.querySelectorAll('[data-dealer-logout]');
  Array.prototype.forEach.call(signOutLinks, function (link) {
    link.addEventListener('click', function (event) {
      event.preventDefault();
      post('/api/dealer/logout', {})
        .catch(function (err) {
          // The cookie may already be gone; send them onward regardless.
          console.error('[DealerAuth ' + reqId + '] logout failed', err);
        })
        .then(function () {
          window.location.assign(link.getAttribute('href') || '/');
        });
    });
  });

  /* ---- Personalisation ---- */
  var companySlots = document.querySelectorAll('[data-dealer-company]');
  if (companySlots.length && window.__dealer && window.__dealer.company) {
    Array.prototype.forEach.call(companySlots, function (slot) {
      var template = slot.getAttribute('data-dealer-company');
      slot.textContent = template ? template.replace('[company]', window.__dealer.company) : window.__dealer.company;
    });
  }
})();
